import { Loader2, MessageSquareOff } from 'lucide-react';

import { AgentChatPanel } from '@/components/agent-chat';
import type { WorkflowMutationEntry } from '@/components/chat/types';
import { usePrimaryAgent } from '@/hooks/queries/agents/use-primary-agent';
import { useWorkflowCanvasSurfaceContext } from '@/hooks/use-workflow-canvas-surface-context';

export function canvasChatPlaceholder(
  nodeDisplayName: string | null | undefined,
): string | undefined {
  return nodeDisplayName ? `Chat about '${nodeDisplayName}'` : undefined;
}

interface CanvasAgentChatProps {
  workflowId?: string | null;
  onApplyMutations?: (
    mutations: WorkflowMutationEntry[],
    targetWorkflowId?: string | null,
    applyKey?: string,
  ) => boolean | void;
  applyMutationsDisabled?: boolean;
  applyMutationsDisabledReason?: string;
  onRunWorkflow?: (targetWorkflowId?: string | null) => Promise<string | null>;
}

export const CanvasAgentChat = ({
  workflowId,
  onApplyMutations,
  applyMutationsDisabled,
  applyMutationsDisabledReason,
  onRunWorkflow,
}: CanvasAgentChatProps) => {
  const { data: agent, isLoading, isError } = usePrimaryAgent();

  const surfaceContext = useWorkflowCanvasSurfaceContext(
    workflowId,
    applyMutationsDisabled,
  );
  const focusedNodeName =
    surfaceContext?.surface === 'workflow_canvas'
      ? surfaceContext.focused_node?.node_display_name
      : undefined;

  if (isLoading) {
    return (
      <div
        data-testid='canvas-chat-loading'
        className='flex flex-1 items-center justify-center text-muted-foreground'
      >
        <Loader2 className='size-4 animate-spin' />
      </div>
    );
  }

  if (isError || !agent) {
    return (
      <div
        data-testid='canvas-chat-unavailable'
        className='flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center'
      >
        <MessageSquareOff className='size-5 text-muted-foreground' />
        <p className='text-sm font-medium text-foreground'>Chat unavailable</p>
        <p className='max-w-60 text-xs text-muted-foreground'>
          We could not resolve an agent for your organization. Try again in a
          moment.
        </p>
      </div>
    );
  }

  // No workflow card above the rail: the chat shows one card per turn, and a
  // run puts the run card in the message that started it.
  return (
    <AgentChatPanel
      agentId={agent.id}
      layout='rail'
      ground='background'
      surfaceContext={surfaceContext}
      openWorkflowId={workflowId}
      composerPlaceholder={canvasChatPlaceholder(focusedNodeName)}
      customInstructionsEnabled
      clarificationSettingsEnabled
      onApplyMutations={onApplyMutations}
      applyMutationsDisabled={applyMutationsDisabled}
      applyMutationsDisabledReason={applyMutationsDisabledReason}
      onRunWorkflow={onRunWorkflow}
    />
  );
};
